/**
 * Route-level authorization. Call from a route's `beforeLoad`; it throws
 * a redirect (to /login or /unauthorized) when the current user fails the
 * check. Lives in the router layer because it couples RBAC to navigation.
 */
import { redirect } from '@tanstack/react-router'
import type { CheckMode, Permission, UserRole } from '@/lib/rbac'
import { ROUTE_PATHS } from '@/config/app.config'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { canAccess, hasMinRole, hasRole } from '@/lib/rbac'

/**
 * Where to send an unauthenticated visitor. Sign-in now lives in-app at
 * `/login`, so always redirect there.
 *
 * Returns a redirect to `throw` from a route's `beforeLoad`.
 */
export function unauthenticatedRedirect() {
  return redirect({ to: ROUTE_PATHS.login })
}

interface RequireAccessOptions {
  permission?: Permission | Array<Permission>
  role?: UserRole | Array<UserRole>
  minRole?: UserRole
  /** How to combine multiple permissions. Default: 'any'. */
  mode?: CheckMode
}

export function requireAccess(options: RequireAccessOptions): void {
  const user = useAuthStore.getState().user
  if (!user) {
    throw unauthenticatedRedirect()
  }

  const { permission, role, minRole, mode } = options

  if (permission) {
    const perms = Array.isArray(permission) ? permission : [permission]
    if (!canAccess(user, perms, mode)) {
      throw redirect({ to: ROUTE_PATHS.unauthorized })
    }
  }
  if (role && !hasRole(user, role)) {
    throw redirect({ to: ROUTE_PATHS.unauthorized })
  }
  if (minRole && !hasMinRole(user, minRole)) {
    throw redirect({ to: ROUTE_PATHS.unauthorized })
  }
}
